import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { cases as casesApi, errorMessage } from '../utils/api';
import { buildZip, collectDropped, collectPicked } from '../utils/zip';
import { formatDateTime, formatNumber } from '../utils/format';
import StudyViewer from './StudyViewer';

/*
 * مطالعه‌ی تصویری روی پرونده — بارگذاری و دریافت.
 *
 * یک کامپوننت برای هر دو مصرف‌کننده: تصویربرداری خروجی دستگاه را می‌گذارد
 * و ادیتور فایل ادیت‌شده را. تفاوتشان فقط در `kind` و متن‌هاست.
 *
 * فولدرِ رهاشده همین‌جا در مرورگر بسته‌بندی می‌شود؛ زیپ آماده هم پذیرفته
 * است. مسیر اصلی در تولید، عاملِ خودکار روی کامپیوتر تصویربرداری است و
 * این پنجره جایگزین دستی آن.
 */

function humanSize(bytes) {
  if (!bytes) return '—';
  const units = ['بایت', 'کیلوبایت', 'مگابایت', 'گیگابایت'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${formatNumber(Math.round(value * 10) / 10)} ${units[unit]}`;
}

export default function StudyPanel({ caseId, kind = 'raw', canUpload = false, title, hint }) {
  // فایلِ ادیت‌شده قالب ثابتی ندارد؛ خروجی دستگاه دارد. تفاوت رفتار
  // انتخاب فایل از همین یک پرچم می‌آید.
  const isEdited = kind === 'edited';
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [stage, setStage] = useState('');       // 'packing' | 'uploading' | ''
  const [viewing, setViewing] = useState(null); // شناسه‌ی مطالعه‌ی باز در نمایشگر
  const [progress, setProgress] = useState(0);
  const fileInput = useRef(null);
  const folderInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await casesApi.listStudies(caseId);
      setStudies(data.filter((row) => row.kind === kind));
    } catch {
      setStudies([]);
    } finally {
      setLoading(false);
    }
  }, [caseId, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const send = useCallback(
    async (entries) => {
      if (!entries.length) {
        toast.error(
          isEdited
            ? 'فایلی پیدا نشد. خروجی کارتان را بگذارید.'
            : 'فایلی پیدا نشد. فولدر خروجی دستگاه را بگذارید.'
        );
        return;
      }

      try {
        let blob;
        let filename;

        // اگر خودش یک زیپ است، دست نمی‌زنیم — دوباره بسته‌بندی بی‌فایده است.
        const single = entries.length === 1 ? entries[0].file : null;
        if (single && single.name.toLowerCase().endsWith('.zip')) {
          blob = single;
          filename = single.name;
        } else {
          setStage('packing');
          setProgress(0);
          blob = await buildZip(entries, (done, total) =>
            setProgress(Math.round((done / total) * 100))
          );
          const root = entries[0].path.split('/')[0];
          filename = `${root || 'study'}.zip`;
        }

        setStage('uploading');
        setProgress(0);
        const { data } = await casesApi.uploadStudy(caseId, blob, {
          kind,
          filename,
          onProgress: (done, total) => setProgress(Math.round((done / total) * 100)),
        });

        if (data.warning) {
          // خطا نیست: فایل بالا رفته. ولی باید دیده شود — این تنها جایی
          // است که «اسکن بیمار دیگر روی این پرونده» گرفته می‌شود.
          toast.warning(data.warning, { autoClose: 15000 });
        } else {
          toast.success('فایل روی سرور نشست.');
        }
        await load();
      } catch (error) {
        toast.error(errorMessage(error, 'بارگذاری انجام نشد.'));
      } finally {
        setStage('');
        setProgress(0);
      }
    },
    [caseId, kind, isEdited, load]
  );

  const onDrop = async (event) => {
    event.preventDefault();
    setDragging(false);
    if (stage) return;
    send(await collectDropped(event.dataTransfer));
  };

  const onPick = (event) => {
    // FileList زنده است: اول کپی، بعد پاک کردن ورودی.
    const entries = collectPicked(event.target.files);
    event.target.value = '';
    send(entries);
  };

  const busy = Boolean(stage);

  return (
    <section className="studies">
      <div className="studies__head">
        <h4 className="studies__title">{title}</h4>
        {hint && <p className="studies__hint">{hint}</p>}
      </div>

      {canUpload && (
        <div
          className={`dropzone ${dragging ? 'is-over' : ''} ${busy ? 'is-busy' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            if (!busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          {busy ? (
            <>
              <p className="dropzone__title">
                {stage === 'packing' ? 'در حال بسته‌بندی…' : 'در حال بارگذاری…'}
              </p>
              <div className="dropzone__bar">
                <span style={{ width: `${progress}%` }} />
              </div>
              <p className="dropzone__note num">{formatNumber(progress)}٪</p>
              {stage === 'packing' && (
                <p className="dropzone__note">
                  فایل‌ها فشرده نمی‌شوند؛ تصاویر دایکام از قبل فشرده‌اند.
                </p>
              )}
            </>
          ) : (
            <>
              <span className="dropzone__icon" aria-hidden="true">🦷</span>
              <p className="dropzone__title">
                {isEdited
                  ? 'فایل، فولدر یا زیپ را اینجا رها کنید'
                  : 'فولدر یا فایل زیپ را اینجا رها کنید'}
              </p>
              <p className="dropzone__note">
                {isEdited ? (
                  'هر قالبی — ورد، تصویر، PDF. چند فایل با هم هم می‌شود.'
                ) : (
                  <>
                    پوشه‌ی <code>Cache</code> خودکار کنار گذاشته می‌شود.
                  </>
                )}
              </p>
              <div className="dropzone__actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => folderInput.current?.click()}
                >
                  انتخاب فولدر
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => fileInput.current?.click()}
                >
                  {isEdited ? 'انتخاب فایل' : 'انتخاب فایل زیپ'}
                </button>
              </div>
            </>
          )}

          {/* webkitdirectory با JSX camelCase شناخته نمی‌شود؛ باید همین شکل باشد. */}
          <input
            ref={folderInput}
            type="file"
            hidden
            webkitdirectory=""
            directory=""
            multiple
            onChange={onPick}
          />
          {/*
            خروجی دستگاه همیشه زیپ است، ولی فایلِ ادیت‌شده هر قالبی دارد —
            ورد، JPEG، PDF. محدود کردنش به زیپ یعنی ادیتور نتواند کارش را
            تحویل دهد. آنچه انتخاب شود در مرورگر بسته‌بندی می‌شود.
          */}
          <input
            ref={fileInput}
            type="file"
            hidden
            multiple={isEdited}
            accept={isEdited ? undefined : '.zip'}
            onChange={onPick}
          />
        </div>
      )}

      {loading ? (
        <p className="studies__empty">در حال بارگذاری…</p>
      ) : studies.length === 0 ? (
        <p className="studies__empty">هنوز فایلی بارگذاری نشده است.</p>
      ) : (
        <ul className="studies__list">
          {studies.map((study) => (
            <li key={study.id} className="study">
              <div className="study__main">
                <span className="study__name">{study.original_name || 'آرشیو مطالعه'}</span>
                <span className="study__meta num">
                  {humanSize(study.size_bytes)}
                  {study.instance_count > 0 && ` · ${formatNumber(study.instance_count)} برش`}
                  {study.series_count > 1 && ` · ${formatNumber(study.series_count)} سری`}
                </span>
                <span className="study__meta">
                  <bdi>{study.uploaded_by_name}</bdi>
                  {study.by_agent && ' (خودکار)'} · {formatDateTime(study.created_at)}
                </span>
                {study.dicom_patient_name && (
                  <span className="study__meta study__meta--dicom">
                    در فایل: <bdi>{study.dicom_patient_name}</bdi>
                    {study.dicom_patient_id && ` · ${study.dicom_patient_id}`}
                  </span>
                )}
              </div>

              <div className="study__actions">
                {study.has_rendition && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setViewing(viewing === study.id ? null : study.id)}
                  >
                    {viewing === study.id ? 'بستن نمایشگر' : '🔍 نمایش'}
                  </button>
                )}
                {study.is_available ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() =>
                      casesApi
                        .downloadStudy(study.id)
                        .catch((error) => toast.error(errorMessage(error, 'دریافت نشد.')))
                    }
                  >
                    دریافت
                  </button>
                ) : (
                  <span className="study__purged" title={formatDateTime(study.purged_at)}>
                    فایل پاک شده
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {viewing && <StudyViewer studyId={viewing} onClose={() => setViewing(null)} />}
    </section>
  );
}
